/**
 * Home Dashboard
 * Main landing page with metrics overview and quick actions
 */

import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Container, styled } from '@mui/material';
import { MetricsCard, QuickActions, WaveBackground, ActivityFeed } from '../components/dashboard';
import { colors } from '../theme/colors';
import { getAllDevices } from '../api/devices';
import { getAllDeviceFamilies } from '../api/device_family';
import { getAllContractors } from '../api/contractors_management';
import DevicesIcon from '@mui/icons-material/Devices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';

// Hero section with gradient background
const HeroSection = styled(Box)({
  position: 'relative',
  background: colors.primary.gradient,
  color: colors.text.inverse,
  padding: '100px 0 140px',
  overflow: 'hidden',
  marginBottom: '-80px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 30% 50%, rgba(121, 134, 203, 0.2), transparent 50%)',
    pointerEvents: 'none',
  },
});

interface DashboardMetrics {
  totalDevices: number;
  activeDevices: number;
  deviceFamilies: number;
  contractors: number;
}

export const Home = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalDevices: 0,
    activeDevices: 0,
    deviceFamilies: 0,
    contractors: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch all data in parallel
        const [devices, families, contractors] = await Promise.all([
          getAllDevices(),
          getAllDeviceFamilies(),
          getAllContractors(),
        ]);

        // Calculate metrics
        const totalDevices = devices?.length || 0;
        const activeDevices = devices?.filter((d: any) => d.is_active)?.length || 0;
        const deviceFamilies = families?.length || 0;
        const contractorCount = contractors?.length || 0;

        setMetrics({
          totalDevices,
          activeDevices,
          deviceFamilies,
          contractors: contractorCount,
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background.default }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                mb: 2,
                opacity: 0.8,
              }}
            >
              Платформа управления
            </Typography>
            <Typography
              variant="h1"
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                letterSpacing: '-0.02em',
              }}
            >
              byonsides Aqua
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 400,
                opacity: 0.85,
                fontSize: { xs: '1.125rem', md: '1.375rem' },
                maxWidth: '600px',
              }}
            >
              Мониторинг и управление IoT устройствами в реальном времени
            </Typography>
          </Box>
        </Container>
        <WaveBackground opacity={0.12} />
      </HeroSection>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 6, position: 'relative', zIndex: 2 }}>
        {/* Metrics Overview */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              mb: 4,
              fontSize: '1.5rem',
            }}
          >
            Обзор системы
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricsCard
                label="Всего устройств"
                value={metrics.totalDevices}
                icon={<DevicesIcon />}
                color="primary"
                animationDelay={0}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricsCard
                label="Активные устройства"
                value={metrics.activeDevices}
                icon={<CheckCircleIcon />}
                color="success"
                animationDelay={100}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricsCard
                label="Семейства устройств"
                value={metrics.deviceFamilies}
                icon={<CategoryIcon />}
                color="primary"
                animationDelay={200}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricsCard
                label="Контракторы"
                value={metrics.contractors}
                icon={<PeopleIcon />}
                color="warning"
                animationDelay={300}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              mb: 4,
              fontSize: '1.5rem',
            }}
          >
            Быстрые действия
          </Typography>
          <QuickActions />
        </Box>

        {/* Activity Feed - commented out until we have real activity data
        <Box sx={{ mb: 6 }}>
          <ActivityFeed activities={[]} />
        </Box>
        */}
      </Container>
    </Box>
  );
};
